import "./Navbar.scss";
import { useState, useEffect, useRef } from "react";
import TopBar from "./TopBar";
import MainNavSection from "./MainNavSection";
import { useLanguage } from "../../context/LanguageContext";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [hideTopBar, setHideTopBar] = useState(false);
  const prevScrollY = useRef(0);
  const scrollThreshold = 50;
  const navbarRef = useRef(null);
  const topBarHeight = useRef(0);
  const { dir } = useLanguage();

  // Handle screen resize for responsive behaviors
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Measure the top bar height for accurate positioning
  useEffect(() => {
    if (!isMobile) {
      const topBarElement = document.querySelector(".navbar__top");
      if (topBarElement) {
        topBarHeight.current = topBarElement.offsetHeight;
      }
    }
  }, [isMobile]);

  // Handle scroll to hide/show the top navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Don't hide the top bar if mobile menu is open
      if (mobileMenuOpen) {
        setHideTopBar(false);
        return;
      }

      // If we're scrolling down and we've scrolled past the threshold
      if (
        currentScrollY > prevScrollY.current &&
        currentScrollY > scrollThreshold
      ) {
        setHideTopBar(true);
      }
      // If we're scrolling up or near the top
      else if (currentScrollY < prevScrollY.current || currentScrollY < 10) {
        setHideTopBar(false);
      }

      prevScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    if (!isMobile && !isTablet) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, isTablet]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);

    // When opening mobile menu, always show top bar
    if (!mobileMenuOpen) {
      setHideTopBar(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && mobileMenuOpen) {
        toggleMobileMenu();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [mobileMenuOpen]);

  return (
    <div
      ref={navbarRef}
      className={`navbar ${mobileMenuOpen ? "menu-open" : ""} ${
        hideTopBar ? "hide-top-bar" : ""
      } ${dir === "rtl" ? "rtl" : "ltr"}`}
    >
      {/* TopBar Component */}
      <TopBar isMobile={isMobile} isTablet={isTablet} />

      {/* Main Navigation Section */}
      <MainNavSection
        isMobile={isMobile}
        isTablet={isTablet}
        mobileMenuOpen={mobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
      />
    </div>
  );
};

export default Navbar;
